import type { RefObject } from "react";
import { useCallback, useState, useEffect } from "react";

type Props = {
  ref: RefObject<HTMLDivElement>;
  isSpSize: boolean;
};

/** ArrowButton width * 2 */
const PC_BUTTON_WIDTH = 64;
const SP_BUTTON_WIDTH = 48;
export const useCategoryCarousel = (props: Props) => {
  const { ref, isSpSize } = props;

  const BUTTON_WIDTH = isSpSize ? SP_BUTTON_WIDTH : PC_BUTTON_WIDTH;

  const [isShowPrevButton, setIsShowPrevButton] = useState(false);
  const [isShowNextButton, setIsShowNextButton] = useState(true);
  const [scrollAmount, setScrollAmount] = useState(0);

  const onPrevButtonClicked = useCallback(() => {
    if (!ref.current) return;
    const scope = ref.current;
    const { scrollLeft } = scope;

    if (scrollLeft === 0) return;

    scope.scrollTo({ left: scrollLeft - scrollAmount, behavior: "smooth" });
  }, [ref, scrollAmount]);

  const onNextButtonClicked = useCallback(() => {
    if (!ref.current) return;
    const scope = ref.current;
    const { scrollLeft, scrollWidth, clientWidth } = scope;

    if (scrollWidth - Math.ceil(scrollLeft) === clientWidth) return;

    scope.scrollTo({ left: scrollLeft + scrollAmount, behavior: "smooth" });
  }, [ref, scrollAmount]);

  const handleHorizontalScroll = () => {
    if (!ref.current) return;
    const scope = ref.current;

    const { scrollLeft, scrollWidth, clientWidth } = scope;

    // スクロールが左端にある場合は非表示
    setIsShowPrevButton(scrollLeft !== 0);

    // スクロールが右端にある場合またはスクロールが不要な場合非表示
    const isMaxScrolledLeft = scrollWidth <= Math.ceil(scrollLeft) + clientWidth;

    setIsShowNextButton(!isMaxScrolledLeft);
  };

  useEffect(() => {
    if (!ref.current) return;
    const scope = ref.current;
    scope.addEventListener("scroll", handleHorizontalScroll, { passive: true });
    return () => {
      scope.removeEventListener("scroll", handleHorizontalScroll);
    };
  }, []);

  /** 画面幅よりもスクロール幅が小さい時、ArrowRightを非表示にする処理 */
  const requireDisplayForArrowRight = () => {
    const targetRef = ref.current;
    if (!targetRef) return;
    queueMicrotask(() => {
      window.innerWidth < targetRef.scrollWidth ? setIsShowNextButton(true) : setIsShowNextButton(false);
    });
  };

  // ボタン押下時にスクロールする移動量を取得
  const initScrollAmount = () => {
    if (ref.current === null) return;
    setScrollAmount(window.innerWidth - BUTTON_WIDTH);
  };

  const resizeListener = () => {
    initScrollAmount();
    requireDisplayForArrowRight();
  };

  useEffect(() => {
    initScrollAmount();
    requireDisplayForArrowRight();
  }, [isSpSize]);

  useEffect(() => {
    window.addEventListener("resize", resizeListener, { passive: true });
    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  return {
    isShowPrevButton,
    isShowNextButton,
    onPrevButtonClicked,
    onNextButtonClicked,
  };
};
